import React, { VFC } from 'react';
import { differenceInDays, differenceInMonths, differenceInYears, subMinutes, subMonths } from 'date-fns';
import { stringToDate } from '@dxb-web-ui-framework/common/utils/Date';
import { FilteringChip, Box } from '@dxb-web-ui-framework/design-system';
import { FormConfig, ComponentSchema } from '@dxb-web-ui-framework/content-composer/components/FormBuilder/types';
import { GenericObject, Nullable } from '@dxb-web-ui-framework/common/types';

export type SelectedFiltersChipProps = {
  filtersConfig: FormConfig;
  filters: { [key: string]: any };
  onChangeFilters: (newFilters: { [key: string]: any }) => void;
  shouldResetFromAndToDatesToInitialAndDisableChip?: boolean;
};

const getFilterConfigComponent = (filterKey: string, filtersConfig: FormConfig) =>
  filtersConfig.components?.find(component =>
    [
      component.key,
      component.properties?.filters?.rangeReference?.from,
      component.properties?.filters?.rangeReference?.to,
    ].includes(filterKey),
  );

const getFilterValue = (
  filterKey: string,
  filterConfigComponent: Nullable<ComponentSchema>,
  filters: GenericObject,
): string => {
  const valueFormatter = filterConfigComponent?.properties?.filters?.valueFormatter ?? null;

  if (filterConfigComponent?.properties?.filters?.rangeReference) {
    return typeof valueFormatter === 'function'
      ? valueFormatter({
          from: filters[filterConfigComponent?.properties?.filters?.rangeReference.from],
          to: filters[filterConfigComponent?.properties?.filters?.rangeReference.to],
        })
      : `${filters[filterConfigComponent?.properties?.filters?.rangeReference.from]} - ${
          filters[filterConfigComponent?.properties?.filters?.rangeReference.to]
        }`;
  }

  return typeof valueFormatter === 'function'
    ? valueFormatter(filters[filterKey])
    : filters[filterKey].label
    ? filters[filterKey].label
    : filters[filterKey];
};

const SelectedFiltersChip: VFC<SelectedFiltersChipProps> = ({
  filtersConfig,
  filters,
  onChangeFilters,
  shouldResetFromAndToDatesToInitialAndDisableChip = false,
}) => {
  const onDeleteFilter = (filterKey: string) => {
    const filterConfigComponent = getFilterConfigComponent(filterKey, filtersConfig);
    if (filterConfigComponent?.properties?.filters?.rangeReference) {
      if (shouldResetFromAndToDatesToInitialAndDisableChip) {
        filters['fromTrxDate'] = stringToDate({
          date: subMonths(new Date(), 2).toISOString(),
          formatExp: "yyyy-MM-dd'T'00:00:00",
        });
        filters['toTrxDate'] = stringToDate({
          date: subMinutes(new Date(), 1).toISOString(),
          formatExp: "yyyy-MM-dd'T'HH:mm:00",
          tz: 'UTC',
        });
      }
      if (filterConfigComponent.properties.filters.rangeReference.from !== 'fromTrxDate') {
        delete filters[filterConfigComponent?.properties?.filters?.rangeReference.from];
        delete filters[filterConfigComponent?.properties?.filters?.rangeReference.to];
      }
    } else {
      delete filters[filterKey];
    }
    onChangeFilters(filters);
  };

  const filteredChips = Object.entries(filters).reduce((acc, [filterKey, filterValue]) => {
    const filterConfigComponent = getFilterConfigComponent(filterKey, filtersConfig);
    if (filterConfigComponent && !acc.find(({ label }) => label === filterConfigComponent?.label)) {
      acc.push({
        key: filterKey,
        label: filterConfigComponent?.label ?? '',
        value: getFilterValue(filterKey, filterConfigComponent, filters),
      });
    }

    return acc;
  }, [] as { key: string; label: string; value: string }[]);

  const isFilteredChipDisabled = (key: string) => {
    if (key === 'fromTrxDate' && shouldResetFromAndToDatesToInitialAndDisableChip) {
      const fromDate = new Date(filters?.fromTrxDate);
      const toDate = new Date(filters?.toTrxDate);
      const diffInDays = differenceInDays(toDate, fromDate);
      const diffInMonths = differenceInMonths(toDate, fromDate);
      const diffInYears = differenceInYears(toDate, fromDate);

      if (diffInDays === 59 && diffInMonths === 2 && diffInYears === 0) {
        return true;
      }

      return false;
    }

    return false;
  };

  return (
    <>
      {filteredChips.map(({ key, label, value }, index) => (
        <Box key={key} component='span' mr={filteredChips.length !== index - 1 ? 1 : 0}>
          <FilteringChip
            label={`${label}: ${value}`}
            onDelete={() => onDeleteFilter(key)}
            disabled={isFilteredChipDisabled(key)}
          />
        </Box>
      ))}
    </>
  );
};

export { SelectedFiltersChip };
